import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { Phone, Mail, MapPin, Send, Check } from "lucide-react";
import { usePublicSettings } from "../lib/publicSettings";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PushOptIn } from "./PushOptIn";

export const Footer = () => {
  const settings = usePublicSettings();
  const phone = settings?.contact_phone || "";
  const email = settings?.contact_email || "";
  const address = settings?.contact_address || "";
  const hours = (settings?.opening_hours || "").trim();
  const [nlEmail, setNlEmail] = useState("");
  const [nlStatus, setNlStatus] = useState("idle"); // idle | loading | done

  const subscribe = async (e) => {
    e.preventDefault();
    const val = nlEmail.trim();
    if (!val || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      toast.error("Inserisci un'email valida");
      return;
    }
    setNlStatus("loading");
    try {
      const r = await api.post("/newsletter/subscribe", { email: val, source: "footer" });
      if (r.data.already_subscribed) {
        toast.info("Sei già iscritto/a alla newsletter");
      } else {
        toast.success("Iscrizione riuscita! Controlla la tua email");
      }
      try { localStorage.setItem("guzz_newsletter_subscribed", "1"); } catch (_) {}
      setNlStatus("done");
      setNlEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore iscrizione, riprova");
      setNlStatus("idle");
    }
  };

  return (
    <footer
      className="mt-24 bg-midnight text-white relative overflow-hidden"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-4 gap-12">
        <div>
          <Logo size="md" tone="light" />
          <p className="mt-6 text-sm text-white/60 leading-relaxed max-w-xs">
            Auto usate di qualità selezionate da Rocco Andrea Guzzardella.
          </p>
        </div>

        <div>
          <h3 className="text-gold text-sm uppercase tracking-widest mb-6 font-sans font-semibold">
            Contatti
          </h3>
          <ul className="space-y-3 text-sm text-white/80" data-testid="footer-contacts">
            {phone && (
              <li className="flex items-center gap-3">
                <Phone size={14} className="text-gold shrink-0" />
                <a href={`tel:${phone.replace(/\s+/g, "")}`} className="hover:text-gold transition-colors">
                  {phone}
                </a>
              </li>
            )}
            {email && (
              <li className="flex items-center gap-3">
                <Mail size={14} className="text-gold shrink-0" />
                <a href={`mailto:${email}`} className="hover:text-gold transition-colors break-all">
                  {email}
                </a>
              </li>
            )}
            {address && (
              <li className="flex items-start gap-3">
                <MapPin size={14} className="text-gold shrink-0 mt-0.5" />
                <span className="whitespace-pre-line">{address}</span>
              </li>
            )}
            {!phone && !email && !address && (
              <li className="text-white/40 italic">Contatti non ancora inseriti</li>
            )}
          </ul>
        </div>

        <div>
          <h3 className="text-gold text-sm uppercase tracking-widest mb-6 font-sans font-semibold">
            Orari
          </h3>
          {hours ? (
            <div className="text-sm text-white/80 whitespace-pre-line leading-relaxed" data-testid="footer-hours">
              {hours}
            </div>
          ) : (
            <ul className="space-y-2 text-sm text-white/80">
              <li className="flex justify-between"><span>Lun - Ven</span><span>9:00 - 19:00</span></li>
              <li className="flex justify-between"><span>Sabato</span><span>9:00 - 13:00</span></li>
              <li className="flex justify-between"><span>Domenica</span><span>Chiuso</span></li>
            </ul>
          )}
        </div>

        <div data-testid="footer-newsletter">
          <h3 className="text-gold text-sm uppercase tracking-widest mb-6 font-sans font-semibold">
            Novità in Anteprima
          </h3>
          <p className="text-sm text-white/60 leading-relaxed mb-4">
            Lascia la tua email: ti avvisiamo appena arrivano auto nuove nel parco.
          </p>
          {nlStatus === "done" ? (
            <div className="flex items-center gap-2 text-sm text-gold" data-testid="newsletter-success">
              <Check size={16} strokeWidth={2.5} /> Sei nel nostro parco preferiti
            </div>
          ) : (
            <form onSubmit={subscribe} className="flex flex-col gap-2">
              <input
                type="email"
                required
                value={nlEmail}
                onChange={(e) => setNlEmail(e.target.value)}
                placeholder="La tua email"
                className="bg-white/5 border border-white/15 rounded-sm px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-gold transition-colors"
                data-testid="newsletter-email-input"
                disabled={nlStatus === "loading"}
              />
              <button
                type="submit"
                disabled={nlStatus === "loading"}
                className="btn-gold flex items-center justify-center gap-2 py-2.5 text-xs uppercase tracking-widest font-semibold disabled:opacity-60"
                data-testid="newsletter-submit-btn"
              >
                <Send size={13} />
                {nlStatus === "loading" ? "Invio..." : "Iscriviti"}
              </button>
              <p className="text-[10px] text-white/40 leading-relaxed mt-1">
                Niente spam. Solo nuove auto e occasioni. Puoi disiscriverti quando vuoi.
              </p>
            </form>
          )}
          <PushOptIn />
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex justify-center items-center gap-2 text-xs text-white/40">
          <p>© {new Date().getFullYear()} GuzZARdella Auto. Tutti i diritti riservati.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
